import { Injectable, Injector, computed, inject, signal } from '@angular/core';
// Imported for its INJECTION TOKEN only. `injector.get()` below runs at call
// time; the class is never injected in a constructor here. See `resolve()`.
import { TranslateService } from '@ngx-translate/core';

export type NotifyKind = 'success' | 'error' | 'warning' | 'info';

export interface UiToast {
  id: number;
  kind: NotifyKind;
  text: string;
}

/** How long a toast stays up. 8s — the value ngx-toastr was configured with. */
const TIMEOUT_MS = 8000;

/** Per-toast lifetime, so a consumer can keep a duration it already had.
 *  `undefined` uses TIMEOUT_MS. */
export interface NotifyOptions {
  durationMs?: number;
}

/**
 * ONE feedback channel.
 *
 * Replaces `ngx-toastr` (20 call sites in the vendor portal) and the three
 * `MatSnackBar.open()` uses, with the same method names the admin dashboard's
 * NotifyService already exposes, so `toastr.error(x)` -> `notify.error(x)` is a
 * rename and nothing more. Same message, same trigger, same 8s timeout, same
 * "duplicates are collapsed" behaviour ngx-toastr was configured with
 * (`preventDuplicates: true, countDuplicates: true, resetTimeoutOnDuplicate`).
 *
 * TRANSLATION IS LAZY AND OPTIONAL. Passing a plain string shows it verbatim
 * (that is what every existing call site does — they translate first). Passing
 * a key that resolves through TranslateService shows the translation. The
 * TranslateService is resolved through the Injector AT CALL TIME, never in the
 * constructor: `error-feedback.interceptor.ts` documents why — eagerly
 * injecting it from an interceptor builds a construction-time cycle
 * (interceptor -> TranslateService -> HttpClient) that corrupts the injector
 * and surfaces as untranslated keys plus a crash on the first toast.
 */
@Injectable({ providedIn: 'root' })
export class NotifyService {
  private readonly injector = inject(Injector);
  private readonly _toasts = signal<UiToast[]>([]);
  private seq = 0;

  readonly toasts = computed(() => this._toasts());

  success(msgOrKey: string, params?: Record<string, unknown>, opts?: NotifyOptions): void {
    this.push('success', msgOrKey, params, opts);
  }

  error(msgOrKey: string, params?: Record<string, unknown>, opts?: NotifyOptions): void {
    this.push('error', msgOrKey, params, opts);
  }

  /** ngx-toastr's fourth level. `branches` uses it for five soft warnings that
   *  are not failures — keeping the level keeps the meaning. */
  warning(msgOrKey: string, params?: Record<string, unknown>, opts?: NotifyOptions): void {
    this.push('warning', msgOrKey, params, opts);
  }

  info(msgOrKey: string, params?: Record<string, unknown>, opts?: NotifyOptions): void {
    this.push('info', msgOrKey, params, opts);
  }

  dismiss(id: number): void {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  clear(): void {
    this._toasts.set([]);
  }

  private push(
    kind: NotifyKind,
    msgOrKey: string,
    params?: Record<string, unknown>,
    opts?: NotifyOptions,
  ): void {
    const text = this.resolve(msgOrKey, params);
    if (!text) {
      return;
    }
    // ngx-toastr ran with preventDuplicates + resetTimeoutOnDuplicate: an
    // identical message already on screen restarts its timer instead of
    // stacking a second copy. Same here.
    const existing = this._toasts().find((t) => t.kind === kind && t.text === text);
    if (existing) {
      this.arm(existing.id, opts?.durationMs);
      return;
    }
    const id = ++this.seq;
    this._toasts.update((list) => [...list, { id, kind, text }]);
    this.arm(id, opts?.durationMs);
  }

  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  private arm(id: number, durationMs?: number): void {
    const prev = this.timers.get(id);
    if (prev) {
      clearTimeout(prev);
    }
    this.timers.set(
      id,
      setTimeout(() => {
        this.timers.delete(id);
        this.dismiss(id);
      }, durationMs ?? TIMEOUT_MS),
    );
  }

  /** Translate lazily; fall back to the raw string when there is no match. */
  private resolve(msgOrKey: string, params?: Record<string, unknown>): string {
    if (!msgOrKey) {
      return '';
    }
    try {
      const translate = this.injector.get(TranslateService, null);
      if (!translate) {
        return msgOrKey;
      }
      const out = translate.instant(msgOrKey, params);
      return typeof out === 'string' && out ? out : msgOrKey;
    } catch {
      return msgOrKey;
    }
  }
}
